import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Stethoscope } from "lucide-react";

import BottomSheet from "../../app/BottomSheet";
import Input from "src/components/app/Input";
import SearchInput from "src/components/app/SearchInput";
import Button from "src/components/app/Button";
import Loader from "src/components/common/Loader";
import NoData from "src/components/app/NoData";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as doctorActions from "src/redux/slices/doctorsSlice";
import { doctors } from "src/types";

interface DoctorsListBottomSheetProps {
  show: boolean;
  handleClose: () => void;
  onSelect: (doctor: doctors.Doctor) => void;
  stateId: string;
  stateName: string;
  cityId: string;
  cityName: string;
  clinicId: string;
}

const DoctorsListBottomSheet: React.FC<DoctorsListBottomSheetProps> = ({
  show,
  handleClose,
  onSelect,
  stateId,
  stateName,
  cityId,
  cityName,
  clinicId,
}) => {
  const [searchText, setSearchText] = useState("");
  const dispatch = useAppDispatch();
  const { doctorsList, addDoctor } = useAppSelector((state) => state.doctors);

  const [renderView, setRenderView] = useState<"list" | "add">("list");
  const [doctorName, setDoctorName] = useState("");

  const getDoctorsList = () => {
    if (!clinicId) return;
    try {
      const payload = {
        clinic_id: clinicId,
        page: 1,
        search_param: searchText,
        source: "Anyra Care Program",
      };
      dispatch(
        doctorActions.getDoctorsListRequest({
          payload,
          resolve: () => {},
          reject: () => {},
        })
      );
    } catch (error) {
      console.error("error", error);
    }
  };

  useEffect(() => {
    if (show && clinicId) {
      getDoctorsList();
    }
  }, [show, clinicId, searchText]);

  // Handle add doctor success: select new doctor and close
  useEffect(() => {
    if (!addDoctor.isLoading && !addDoctor.error && addDoctor.data) {
      const newDoctor = Array.isArray(addDoctor.data)
        ? addDoctor.data[0]
        : addDoctor.data;
      if (newDoctor?.doctor_id || newDoctor?.name) {
        onSelect(newDoctor as doctors.Doctor);
        handleClose();
        setRenderView("list");
        setDoctorName("");
        dispatch(doctorActions.addDoctorReset());
      }
    }
  }, [addDoctor.isLoading, addDoctor.error, addDoctor.data]);

  // Reset add doctor state when sheet closes
  useEffect(() => {
    if (!show) {
      dispatch(doctorActions.addDoctorReset());
    }
  }, [show]);

  const handleAddDoctor = () => {
    try {
      const payload: doctors.AddDoctorPayload = {
        state_id: stateId,
        state_name: stateName,
        city_id: cityId,
        city_name: cityName,
        clinic_id: clinicId,
        dr_name: doctorName,
        source: "Anyra Care Program",
        specialization: "Oncology",
      };
      dispatch(
        doctorActions.addDoctorRequest({
          payload,
          resolve: () => {},
          reject: () => {},
        })
      );
    } catch (error) {
      console.error("error", error);
    }
  };

  const handleSelectDoctor = (doctor: doctors.Doctor) => {
    onSelect(doctor);
    handleClose();
  };

  const handleCloseSheet = () => {
    setRenderView("list");
    setSearchText("");
    setDoctorName("");
    handleClose();
  };

  return (
    <BottomSheet
      show={show}
      title={renderView === "list" ? "Select Doctor" : "Add Doctor"}
      handleClose={handleCloseSheet}
    >
      <div className="flex flex-col p-4 gap-4">
        {renderView === "list" && (
          <>
            <div>
              <SearchInput
                name="doctor"
                label="Doctor's name"
                type="text"
                placeholder="Enter doctor's name"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <button
              className="text-[var(--app-primary)] text-sm flex items-center gap-1 border-b border-[var(--app-primary)] w-fit pb-1"
              onClick={() => setRenderView("add")}
            >
              Add Doctor
              <ArrowRight className="w-4 h-4" />
            </button>

            <div className="max-h-[300px] overflow-y-auto">
              {doctorsList?.isLoading ? (
                <div className="flex justify-center items-center py-10">
                  <Loader size="lg" color="primary" />
                </div>
              ) : doctorsList?.data?.length > 0 ? (
                doctorsList?.data?.map((doctor: doctors.Doctor) => (
                  <button
                    key={doctor?.doctor_id}
                    className="w-full px-4 py-2 hover:bg-gray-50 text-left text-md text-gray-700 border-b border-gray-200"
                    onClick={() => handleSelectDoctor(doctor)}
                  >
                    {doctor?.name}
                  </button>
                ))
              ) : doctorsList?.data?.length === 0 && !doctorsList?.isLoading ? (
                <NoData
                  icon={<Stethoscope />}
                  title={
                    !searchText
                      ? "Type doctor name to search or add new doctor"
                      : "No doctors found"
                  }
                  className="pb-8"
                />
              ) : null}
            </div>
          </>
        )}
        {renderView === "add" && (
          <>
            <button
              className="flex items-center gap-1 text-sm text-gray-500 -ml-1 mb-2"
              onClick={() => setRenderView("list")}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Doctors list
            </button>

            <Input
              name="doctor"
              label="Doctor's name"
              placeholder="Enter doctor's name"
              value={doctorName}
              onChange={(e) => setDoctorName(e.target.value)}
              fullWidth
              type="text"
              startAdornment={<span className="text-gray-500 ml-1">Dr.</span>}
            />

            <Button
              onClick={handleAddDoctor}
              disabled={!doctorName || addDoctor.isLoading}
              loading={addDoctor.isLoading}
            >
              Add Doctor
            </Button>
          </>
        )}
      </div>
    </BottomSheet>
  );
};

export default DoctorsListBottomSheet;
